import type { SourceMeta, SourceId } from '@/types/market';
import { featureFlags } from '@/config/featureFlags';

/**
 * Registry of every source the app knows about, required and optional.
 * `enabled` is derived from feature flags for the optional portal sources
 * so there is exactly one place that decides whether Redfin/Zillow run.
 */
export const SOURCE_REGISTRY: Record<SourceId, SourceMeta> = {
  'realtor-com-live': {
    id: 'realtor-com-live',
    label: 'Realtor.com Research Data Library (live, via MAY Assistant backend)',
    homepageUrl: 'https://www.realtor.com/research/data/',
    license: 'Public research data, fetched live server-side (avoids browser CORS/rate issues), not a static snapshot.',
    enabled: true,
    optional: false,
  },
  trerc: {
    id: 'trerc',
    label: 'Texas Real Estate Research Center',
    homepageUrl: 'https://trerc.tamu.edu/',
    license: 'Public research data, redistributed as a periodic static snapshot.',
    // Sample-only (TRERC has no browser-fetchable public API -- see
    // trercAdapter.ts) -- off by default now that realtor-com-live covers
    // real sale-flow data. See featureFlags.ENABLE_SAMPLE_FALLBACK.
    enabled: featureFlags.ENABLE_SAMPLE_FALLBACK,
    optional: true,
  },
  nar: {
    id: 'nar',
    label: 'National Association of Realtors',
    homepageUrl: 'https://www.nar.realtor/research-and-statistics',
    license: 'Public research data, redistributed as a periodic static snapshot.',
    enabled: featureFlags.ENABLE_SAMPLE_FALLBACK,
    optional: true,
  },
  'census-acs': {
    id: 'census-acs',
    label: 'U.S. Census Bureau (American Community Survey)',
    homepageUrl: 'https://www.census.gov/programs-surveys/acs',
    license: 'U.S. Government public data.',
    // Real, live ACS data IS fetchable directly from api.census.gov -- this
    // adapter just doesn't do that yet (still reads the bundled sample CSV,
    // see censusAcsAdapter.ts), so treat it the same as the other
    // sample-only sources for now rather than claim it's real. Wiring a
    // live ACS county query here is a clean follow-up, not done in this
    // pass.
    enabled: featureFlags.ENABLE_SAMPLE_FALLBACK,
    optional: true,
  },
  fhfa: {
    id: 'fhfa',
    label: 'Federal Housing Finance Agency (House Price Index)',
    homepageUrl: 'https://www.fhfa.gov/data/hpi',
    license: 'U.S. Government public data.',
    enabled: false, // scaffolded, not wired into Phase 1 scoring yet
    optional: true,
  },
  redfin: {
    id: 'redfin',
    label: 'Redfin Data Center',
    homepageUrl: 'https://www.redfin.com/news/data-center/',
    license: 'Redfin Data Center terms of use apply -- verify before enabling.',
    enabled: featureFlags.ENABLE_REDFIN,
    optional: true,
  },
  zillow: {
    id: 'zillow',
    label: 'Zillow Research (ZHVI, neighborhood-level, live via MAY Assistant backend)',
    homepageUrl: 'https://www.zillow.com/research/data/',
    license:
      'Zillow Real Estate Research data: free for public use by consumers, media, analysts, academics and policymakers, with required attribution (zillowgroup.com/developers/api/public-data/real-estate-metrics/).',
    // Real, live data (zillowNeighborhoodMapAdapter.ts, fetched through the
    // backend for the same CORS/bandwidth reasons as realtor-com-live) as
    // of the neighborhood-layer build -- no longer a scaffold. Only
    // contributes 'neighborhood'-level rows (Houston/Dallas-Fort Worth/
    // Austin/San Antonio); county/ZIP/city rows still come from
    // realtor-com-live.
    enabled: featureFlags.ENABLE_ZILLOW,
    optional: true,
  },
};

export function enabledSourceIds(): SourceId[] {
  return (Object.keys(SOURCE_REGISTRY) as SourceId[]).filter((id) => SOURCE_REGISTRY[id].enabled);
}

/**
 * Precedence order used when the same field is present from multiple
 * sources -- real, live data wins over any sample/static source whenever
 * both happen to be enabled at once (e.g. ENABLE_SAMPLE_FALLBACK on for
 * local demoing while the backend is also reachable).
 */
export const SOURCE_PRECEDENCE: SourceId[] = ['realtor-com-live', 'trerc', 'nar', 'redfin', 'zillow', 'fhfa', 'census-acs'];
